import { fileOpen } from 'browser-fs-access';
import { PDFDocumentLoadingTask } from 'pdfjs-dist';
import { TextContent } from 'pdfjs-dist/types/src/display/api';
import { pdfjs } from 'react-pdf';

import { FILE_SIZE_EXCEEDS_LIMIT } from '../../../whiteboard-errors';
import WhiteboardError from '../../../WhiteboardError';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

const PDF_FILESIZE_LIMIT_MB = 50;

export type PdfPageImage = null | {
    blobUrl: string;
    width: number;
    height: number;
    previewBlobUrl: string;
    previewWidth: number;
    previewHeight: number;
};

export type PdfPage = {
    index: number;
    getPageImage: () => Promise<PdfPageImage>;
    getPageTextContent: () => Promise<TextContent>;
};

export type PdfFile = {
    fileName: string;
    totalPages: number;
    pages: PdfPage[];
};

export const getPdfFile = async (): Promise<PdfFile> => {
    // Open a file.
    const file = await fileOpen({
        mimeTypes: ['application/pdf'],
        extensions: ['.pdf'],
        multiple: false
    });

    if (file.size > PDF_FILESIZE_LIMIT_MB * 1000 * 1000) {
        throw new WhiteboardError(`${FILE_SIZE_EXCEEDS_LIMIT}: File size exceeds limit ${PDF_FILESIZE_LIMIT_MB} MB.`);
    }

    const fileUrl = URL.createObjectURL(file);
    const pdfLoadingTask: PDFDocumentLoadingTask = pdfjs.getDocument(fileUrl);
    const pdf = await pdfLoadingTask.promise;

    const totalPages = pdf.numPages;

    // Make it with higher resolution
    const scale = 2;

    const pdfPages: PdfPage[] = [];

    for (let i = 0; i < totalPages; i += 1) {
        const pdfPage: PdfPage = {
            index: i,
            getPageImage: async () => {
                const page = await pdf.getPage(i + 1);

                const viewport = page.getViewport({ scale });

                const canvas = document.createElement('canvas');

                // Prepare canvas using PDF page dimensions
                const canvasContext = canvas.getContext('2d');
                if (canvasContext === null) {
                    return null;
                }

                canvas.height = viewport.height;
                canvas.width = viewport.width;

                // Render PDF page into canvas context
                const renderContext = {
                    canvasContext,
                    viewport
                };

                await page.render(renderContext).promise;

                const blobUrl = canvas.toDataURL('image/png');

                // Calculate the scale factor to fit the image within 100px
                const scaleFactor = 150 / canvas.width;

                // Create a new canvas for the preview image
                const previewCanvas = document.createElement('canvas');
                previewCanvas.width = canvas.width * scaleFactor;
                previewCanvas.height = canvas.height * scaleFactor;

                const previewCanvasContext = previewCanvas.getContext('2d');
                if (previewCanvasContext === null) {
                    return null;
                }

                // Draw the resized image onto the preview canvas
                previewCanvasContext.drawImage(canvas, 0, 0, previewCanvas.width, previewCanvas.height);

                const previewBlobUrl = previewCanvas.toDataURL('image/png');

                return {
                    blobUrl,
                    width: viewport.width / scale,
                    height: viewport.height / scale,
                    previewBlobUrl,
                    previewWidth: previewCanvas.width,
                    previewHeight: previewCanvas.height
                };
            },
            getPageTextContent: async () => {
                const page = await pdf.getPage(i + 1);
                const textContent = await page.getTextContent();
                return textContent;
            }
        };

        pdfPages[i] = pdfPage;
    }

    const pdfFile: PdfFile = {
        fileName: file.name,
        totalPages,
        pages: pdfPages
    };

    return pdfFile;
};
